import { useCallback, useState } from 'react';

import StorageClient, { SessionStorageKey } from 'lib/clients/StorageClient';

/**
 * This hook provides a stateful value retrieved from sessionStorage, scoped by a given key and an optional scope identifier.
 * It allows components to persist state across re-renders and page refreshes, scoped within a session. If a scope is not provided,
 * the hook will behave as a regular useState hook.
 */
export function useSessionStorageMap<T>(
  key: SessionStorageKey,
  scope: string | undefined,
  initialValue: T,
): [T, (data: T) => void] {
  const getValueMap = useCallback(() => {
    const valueMapString = StorageClient.getSessionItem(key);
    const valueMap = valueMapString ? (JSON.parse(valueMapString) as Record<string, T>) : {};
    return valueMap;
  }, [key]);

  const [value, setValue] = useState(() => {
    // if scope is not provided, return the initial value and do not store it in session storage
    if (!scope) return initialValue;

    const sessionStorageValue = getValueMap()[scope];

    if (sessionStorageValue != null) {
      return sessionStorageValue;
    } else {
      return initialValue;
    }
  });

  const setValueWithSessionStorage = useCallback(
    (data: T) => {
      setValue(data);

      // only store in session storage if scope is provided
      if (scope) {
        const nextValueMap = {
          ...getValueMap(), // get up to date value map
          [scope]: data,
        };
        StorageClient.setSessionItem(key, JSON.stringify(nextValueMap));
      }
    },
    [scope, key, getValueMap],
  );

  return [value, setValueWithSessionStorage];
}
